import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from './AuthContext'
import apiService from '@/services/api'

const ApiStatusContext = createContext()
const SiteDataContext = createContext()
const PricingDataContext = createContext()
const ApiMethodsContext = createContext()

export function ApiProvider({ children }) {
  const { isAuthenticated, getUserJWT, user } = useAuth()

  const [dataReady, setDataReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [aggregatedData, setAggregatedData] = useState(null)
  const [pathAggData, setPathAggData] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [siteNames, setSiteNames] = useState(new Map())
  const [siteSources, setSiteSources] = useState(new Map())
  const [siteWhitelist, setSiteWhitelist] = useState([])
  const [unitTypeAreas, setUnitTypeAreas] = useState(new Map())
  const [unitTypePaths, setUnitTypePaths] = useState(null)
  const [priceChanges, setPriceChanges] = useState(new Map())
  const [wtfRecords, setWtfRecords] = useState([])
  const [pipelineStatus, setPipelineStatus] = useState(null)
  const [backendVersion, setBackendVersion] = useState('0.0.0')

  const priceStreamRef = useRef(null)
  const jwtCacheRef = useRef({ token: null, expiresAt: 0 })

  const JWT_CACHE_TTL_MS = 5 * 60 * 1000

  const syncJWT = useCallback(async () => {
    const now = Date.now()
    if (jwtCacheRef.current.token && now < jwtCacheRef.current.expiresAt) {
      return jwtCacheRef.current.token
    }
    try {
      const jwt = await getUserJWT()
      apiService.setJWT(jwt)
      jwtCacheRef.current = { token: jwt, expiresAt: now + JWT_CACHE_TTL_MS }
      return jwt
    } catch {
      jwtCacheRef.current = { token: null, expiresAt: 0 }
      return null
    }
  }, [getUserJWT])

  const initData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await syncJWT()

      const criticalResults = await Promise.allSettled([
        apiService.getSiteNames(),
        apiService.getSiteWhitelist(),
        apiService.getUnitTypeAreas(),
        apiService.getActiveUnitTypePaths(),
        apiService.getPipelineStatus(),
        apiService.getVersionInfo(),
      ])

      const cVal = (i) => criticalResults[i].status === 'fulfilled' ? criticalResults[i].value : null

      const siteNameData = cVal(0)
      if (siteNameData) {
        if (siteNameData instanceof Map) {
          setSiteNames(siteNameData)
          setSiteSources(new Map())
        } else {
          setSiteNames(siteNameData.names || new Map())
          setSiteSources(siteNameData.ingestionSources || new Map())
        }
      }
      if (cVal(1)) setSiteWhitelist(cVal(1))
      if (cVal(2)) setUnitTypeAreas(cVal(2))
      if (cVal(3)) setUnitTypePaths(cVal(3))
      if (cVal(4)) setPipelineStatus(cVal(4))
      const versionInfo = cVal(5)
      if (versionInfo) setBackendVersion(versionInfo.version || '0.0.0')

      const criticalErrors = criticalResults.filter(r => r.status === 'rejected')
      if (criticalErrors.length > 0) {
        criticalErrors.forEach(e => console.warn('API init partial failure:', e.reason?.message || e.reason))
      }

      setDataReady(true)
      setLoading(false)

      const activeUnitTypePaths = cVal(3)
      const sitePaths = activeUnitTypePaths?.unitTypePathsList
        ? [...new Set(activeUnitTypePaths.unitTypePathsList.map(p => p.split('#').slice(0, 3).join('#')))]
        : []

      const unitTypePathsList = activeUnitTypePaths?.unitTypePathsList ?? []
      const aggSitePromise = sitePaths.length > 0
        ? apiService.getAggregatedDataForPaths(sitePaths)
        : Promise.resolve(null)
      const aggPathsPromise = unitTypePathsList.length > 0
        ? apiService.getAggregatedDataForPaths(unitTypePathsList)
        : Promise.resolve(null)

      Promise.allSettled([
        aggSitePromise,
        aggPathsPromise,
        apiService.getWtf(),
      ]).then(deferredResults => {
        const dVal = (i) => deferredResults[i].status === 'fulfilled' ? deferredResults[i].value : null

        const aggData = dVal(0)
        if (aggData) {
          setAggregatedData(new Map(aggData.dataMap))
          setLastUpdated(aggData.processingDate)
        }
        const pathData = dVal(1)
        if (pathData) setPathAggData(new Map(pathData.dataMap))
        if (dVal(2)) setWtfRecords(dVal(2))

        const deferredErrors = deferredResults.filter(r => r.status === 'rejected')
        if (deferredErrors.length > 0) {
          deferredErrors.forEach(e => console.warn('API deferred load failure:', e.reason?.message || e.reason))
        }
      })
    } catch (err) {
      console.error('Failed to initialize data:', err?.message || err, err?.code)
      setError(err)
      setLoading(false)
    }
  }, [syncJWT])

  const initPriceReview = useCallback(async () => {
    try {
      await syncJWT()
      const changes = await apiService.getPriceChanges()
      setPriceChanges(changes)
    } catch (err) {
      console.error('Failed to load price changes:', err)
    }
  }, [syncJWT])

  const startPriceStreamRef = useRef(null)

  const startPriceStream = useCallback(async () => {
    if (!user || priceStreamRef.current) return

    const controller = new AbortController()
    priceStreamRef.current = controller

    try {
      const stream = apiService.watchPriceChanges(
        {
          id: user.id || user.sub || '',
          name: user.name || '',
          email: user.email || '',
        },
        controller.signal,
      )

      // The server pushes live NewPriceChange batches, but those carry only a
      // partial shape (no full PriceChange fields), so they are not merged into
      // priceChanges — matching prior behavior. We keep the stream open so the
      // connection/reconnect path stays exercised.
      for await (const response of stream) {
        void response
      }
      priceStreamRef.current = null
    } catch (err) {
      if (controller.signal.aborted) return
      console.error('Price stream error:', err)
      priceStreamRef.current = null
      setTimeout(() => { startPriceStreamRef.current?.() }, 5000)
    }
  }, [user])

  useEffect(() => {
    startPriceStreamRef.current = startPriceStream
  }, [startPriceStream])

  const stopPriceStream = useCallback(() => {
    if (priceStreamRef.current) {
      priceStreamRef.current.abort()
      priceStreamRef.current = null
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      initData()
    } else {
      setDataReady(false)
      setAggregatedData(null)
      setPathAggData(null)
      setSiteNames(new Map())
      setSiteSources(new Map())
      setSiteWhitelist([])
      setUnitTypeAreas(new Map())
      setUnitTypePaths(null)
      setPriceChanges(new Map())
      setWtfRecords([])
      setPipelineStatus(null)
      jwtCacheRef.current = { token: null, expiresAt: 0 }
      stopPriceStream()
    }
    return stopPriceStream
  }, [isAuthenticated, initData, stopPriceStream])

  const refreshData = useCallback(async () => {
    await initData()
  }, [initData])

  const refreshPriceChanges = useCallback(async () => {
    await initPriceReview()
  }, [initPriceReview])

  const getTimeseries = useCallback(async (path, dataKey) => {
    await syncJWT()
    return apiService.getTimeseries(path, dataKey)
  }, [syncJWT])

  const getManyTimeseries = useCallback(async (pathKeyMap, windowDays = 365) => {
    await syncJWT()
    return apiService.getManyTimeseries(pathKeyMap, windowDays)
  }, [syncJWT])

  const getSiteGeo = useCallback(async (path) => {
    await syncJWT()
    return apiService.getSiteGeo(path)
  }, [syncJWT])

  const getUnitRentals = useCallback(async (path) => {
    await syncJWT()
    return apiService.getUnitRentals(path)
  }, [syncJWT])

  const getAllUnitRentalPriceChanges = useCallback(async () => {
    await syncJWT()
    return apiService.getAllUnitRentalPriceChanges()
  }, [syncJWT])

  const updateRentalPriceChanges = useCallback(async (updates, deployedBy) => {
    await syncJWT()
    return apiService.updateRentalPriceChanges(updates, deployedBy)
  }, [syncJWT])

  const updatePriceChanges = useCallback(async (changes, userDetails) => {
    await syncJWT()
    return apiService.updatePriceChanges(changes, userDetails)
  }, [syncJWT])

  const deployPriceChanges = useCallback(async (changes, userDetails) => {
    await syncJWT()
    await apiService.updatePriceChanges(changes, userDetails)
    await refreshPriceChanges()
  }, [syncJWT, refreshPriceChanges])

  const forceDeployToStorman = useCallback(async () => {
    await syncJWT()
    return apiService.forceDeployToStorman()
  }, [syncJWT])

  const getFlags = useCallback(async (path) => {
    await syncJWT()
    return apiService.getFlags(path)
  }, [syncJWT])

  const getManyFlags = useCallback(async (paths) => {
    await syncJWT()
    return apiService.getManyFlags(paths)
  }, [syncJWT])

  const getRentalPriceHistory = useCallback(async (ecrUnitId, rentalId) => {
    await syncJWT()
    return apiService.getRentalPriceHistory(ecrUnitId, rentalId)
  }, [syncJWT])

  const getAggregatedDataForPaths = useCallback(async (paths) => {
    await syncJWT()
    return apiService.getAggregatedDataForPaths(paths)
  }, [syncJWT])

  const getCompetitorWeights = useCallback(async (siteId) => {
    await syncJWT()
    return apiService.getCompetitorWeights(siteId)
  }, [syncJWT])

  const updateCompetitorWeights = useCallback(async (updates) => {
    await syncJWT()
    return apiService.updateCompetitorWeights(updates)
  }, [syncJWT])

  const statusValue = useMemo(
    () => ({ dataReady, loading, error }),
    [dataReady, loading, error]
  )

  const siteDataValue = useMemo(
    () => ({
      aggregatedData, pathAggData, lastUpdated,
      siteNames, siteSources, siteWhitelist, unitTypeAreas, unitTypePaths,
      wtfRecords, pipelineStatus, backendVersion,
    }),
    [aggregatedData, pathAggData, lastUpdated, siteNames, siteSources, siteWhitelist,
     unitTypeAreas, unitTypePaths, wtfRecords, pipelineStatus, backendVersion]
  )

  const pricingDataValue = useMemo(
    () => ({ priceChanges }),
    [priceChanges]
  )

  const methodsValue = useMemo(
    () => ({
      refreshData, refreshPriceChanges,
      startPriceStream, stopPriceStream,
      getTimeseries, getManyTimeseries, getSiteGeo,
      getUnitRentals, getAllUnitRentalPriceChanges, updateRentalPriceChanges,
      updatePriceChanges, deployPriceChanges, forceDeployToStorman,
      getFlags, getManyFlags, getRentalPriceHistory,
      getAggregatedDataForPaths, getCompetitorWeights, updateCompetitorWeights,
      setWtfRecords,
      api: apiService,
    }),
    [refreshData, refreshPriceChanges, startPriceStream, stopPriceStream,
     getTimeseries, getManyTimeseries, getSiteGeo,
     getUnitRentals, getAllUnitRentalPriceChanges, updateRentalPriceChanges,
     updatePriceChanges, deployPriceChanges, forceDeployToStorman,
     getFlags, getManyFlags, getRentalPriceHistory,
     getAggregatedDataForPaths, getCompetitorWeights, updateCompetitorWeights]
  )

  return (
    <ApiStatusContext.Provider value={statusValue}>
      <SiteDataContext.Provider value={siteDataValue}>
        <PricingDataContext.Provider value={pricingDataValue}>
          <ApiMethodsContext.Provider value={methodsValue}>
            {children}
          </ApiMethodsContext.Provider>
        </PricingDataContext.Provider>
      </SiteDataContext.Provider>
    </ApiStatusContext.Provider>
  )
}

export function useApiStatus() {
  const ctx = useContext(ApiStatusContext)
  if (!ctx) throw new Error('useApiStatus must be used within ApiProvider')
  return ctx
}

export function useSiteData() {
  const ctx = useContext(SiteDataContext)
  if (!ctx) throw new Error('useSiteData must be used within ApiProvider')
  return ctx
}

export function usePricingData() {
  const ctx = useContext(PricingDataContext)
  if (!ctx) throw new Error('usePricingData must be used within ApiProvider')
  return ctx
}

export function useApiMethods() {
  const ctx = useContext(ApiMethodsContext)
  if (!ctx) throw new Error('useApiMethods must be used within ApiProvider')
  return ctx
}

export function useApi() {
  const status = useContext(ApiStatusContext)
  const siteData = useContext(SiteDataContext)
  const pricingData = useContext(PricingDataContext)
  const methods = useContext(ApiMethodsContext)
  if (!status) throw new Error('useApi must be used within ApiProvider')
  return { ...status, ...siteData, ...pricingData, ...methods }
}
